// ReviewsSection.js  (ONE FILE)
import { React, useEffect, useState, useRef } from "react";
import "./reviews.css";

/*
Single-file hook + component.
- 5 default reviews
- scalable: add more to reviewsData
- interactive: active card, next/prev, click-to-select, horizontal scroll buttons
*/

const reviewsData = [
  {
    id: "r1",
    name: "Danielle R.",
    review:
      "Yokai Software completely upgraded our online presence. The new website looks sharper, loads faster, and feels much more professional. Communication was clear the whole way through, and every update was handled quickly.",
    rating: 5,
  },
  {
    id: "r2",
    name: "Marcus T.",
    review:
      "We hired Yokai Software for web design and development, and the result was better than expected. The site is clean, modern, mobile-friendly, and much easier for our customers to use. Very solid work from start to finish.",
    rating: 5,
  },
  {
    id: "r3",
    name: "Priya S.",
    review:
      "What stood out most was how well Yokai Software understood both design and business goals. They did not just make us a nice-looking website. They helped us present our services more clearly and made the whole brand feel stronger.",
    rating: 5,
  },
  {
    id: "r4",
    name: "Anthony M.",
    review:
      "Yokai Software built a custom solution for our business that saved us time and made customer interactions smoother. The attention to detail, responsiveness, and overall quality made this one of the best development experiences we have had.",
    rating: 5,
  },
  {
    id: "r5",
    name: "Samantha L.",
    review:
      "We needed a website that looked professional and actually converted visitors into leads. Yokai Software delivered exactly that. The layout, messaging, and functionality all feel intentional, polished, and built for growth.",
    rating: 5,
  },
];

// SIMPLE HOOK (same names as original)
const useInteractiveReviews = (reviewsInput = reviewsData) => {
  const scrollRef = useRef(null);
  const [reviews, setReviews] = useState(
    Array.isArray(reviewsInput) && reviewsInput.length ? reviewsInput : reviewsData
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (Array.isArray(reviewsInput) && reviewsInput.length) setReviews(reviewsInput);
    else setReviews(reviewsData);
  }, [reviewsInput]);

  useEffect(() => {
    setIndex((i) => {
      const max = Math.max(0, reviews.length - 1);
      return Math.max(0, Math.min(i, max));
    });
  }, [reviews.length]);

  const active = reviews[index] || null;

  const scrollBy = (dx) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dx, behavior: "smooth" });
  };

  const next = () => {
    if (!reviews.length) return;
    setIndex((i) => (i + 1) % reviews.length);
    scrollBy(320);
  };

  const prev = () => {
    if (!reviews.length) return;
    setIndex((i) => (i - 1 + reviews.length) % reviews.length);
    scrollBy(-320);
  };

  const goTo = (i) => {
    const max = Math.max(0, reviews.length - 1);
    setIndex(Math.max(0, Math.min(i, max)));
  };

  const scrollLeft = () => scrollBy(-420);
  const scrollRight = () => scrollBy(420);

  return {
    reviews,
    active,
    index,
    setIndex: goTo,
    next,
    prev,
    goTo,
    scrollRef,
    scrollLeft,
    scrollRight,
  };
};

const ReviewsSection = () => {
  const {
    reviews,
    active,
    index,
    setIndex,
    next,
    prev,
    scrollRef,
    scrollLeft,
    scrollRight,
  } = useInteractiveReviews(reviewsData);

  const [expandedId, setExpandedId] = useState(null);

  return (
    <div id="reviews_module">
      <div id="reviews_header">
        <h1>Reviews</h1>

        <div id="reviews_navBtns">
          <button className="reviews_btn" onClick={prev} aria-label="Previous review">
            &larr;
          </button>
          <button className="reviews_btn" onClick={next} aria-label="Next review">
            &rarr;
          </button>
        </div>
      </div>

      <div id="reviews_active">
        {active && (
          <>
            <div className="reviews_stars">
              {"★".repeat(active.rating || 5)}
              {"☆".repeat(5 - (active.rating || 5))}
            </div>

            <p className="reviews_text">
              "
              {expandedId === active.id
                ? active.review
                : `${active.review.slice(0, 140)}${active.review.length > 140 ? "…" : ""}`}
              "
            </p>

            {active.review.length > 140 && (
              <div
                className="reviews_readMore"
                onClick={() => setExpandedId(expandedId === active.id ? null : active.id)}
              >
                {expandedId === active.id ? "Show Less" : "Read More"}
              </div>
            )}

            <h3 className="reviews_name">- {active.name}</h3>
          </>
        )}
      </div>

      <div id="reviews_stripWrap">
        <button className="strip_btn" onClick={scrollLeft} aria-label="Scroll left">
          &larr;
        </button>

        <div id="reviews_strip" ref={scrollRef}>
          {reviews.map((r, i) => (
            <div
              key={r.id || i}
              className={`review_card ${i === index ? "active" : ""}`}
              onClick={() => setIndex(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setIndex(i)}
            >
              <div className="review_cardStars">
                {"★".repeat(r.rating || 5)}
                {"☆".repeat(5 - (r.rating || 5))}
              </div>
              <p className="review_cardText">"{r.review}"</p>
              <p className="review_cardName">- {r.name}</p>
            </div>
          ))}
        </div>

        <button className="strip_btn" onClick={scrollRight} aria-label="Scroll right">
          &rarr;
        </button>
      </div>
    </div>
  );
};

export default ReviewsSection;
